<template>
  <div class="events-tab" ref="eventsScrollRef">
    <!-- Controls -->
    <div class="events-controls">
      <div class="filter-group">
        <button
          v-for="filter in filters"
          :key="filter.id"
          class="filter-btn"
          :class="{ active: activeFilters.includes(filter.id) }"
          @click="toggleFilter(filter.id)"
        >
          {{ filter.label }}
        </button>
      </div>
      <div class="action-group">
        <button class="icon-btn" @click="clearEvents" title="Clear Events">
          <svg viewBox="0 0 24 24"
fill="none" stroke="currentColor"
stroke-width="2" width="14" height="14">
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
          </svg>
        </button>
        <button class="icon-btn" @click="exportEvents" title="Export Events">
          <svg viewBox="0 0 24 24"
fill="none" stroke="currentColor"
stroke-width="2" width="14" height="14">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7,10 12,15 17,10" />
            <line x1="12" x2="12" y1="15" y2="3" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Events List -->
    <div class="events-list">
      <div v-for="(event, index) in filteredEvents"
:key="index" class="event-item"
:class="event.type">
        <div class="event-header">
          <span class="event-type-badge" :class="event.type">{{ event.type }}</span>
          <span class="event-name">{{ event.name }}</span>
          <span class="event-time">{{ formatTime(event.timestamp) }}</span>
        </div>
        <div v-if="event.payload" class="event-payload">
          <pre>{{ formatPayload(event.payload) }}</pre>
        </div>
      </div>

      <div v-if="filteredEvents.length === 0" class="empty-state">
        <svg viewBox="0 0 24 24"
fill="none" stroke="currentColor"
stroke-width="2" width="24" height="24">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        <span>No events captured</span>
        <span class="hint">Events will appear here as they occur</span>
      </div>
    </div>

    <!-- Stats -->
    <div class="events-stats">
      <span class="stat">
        <span class="stat-value">{{ events.length }}</span>
        <span class="stat-label">total</span>
      </span>
      <span class="stat">
        <span class="stat-value">{{ filteredEvents.length }}</span>
        <span class="stat-label">shown</span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from "vue";
import { useEventLog } from "@/composables/useEventLog";

const { events, clearEvents } = useEventLog();

const eventsScrollRef = ref<HTMLElement | null>(null);

// Filters
const filters = [
  { id: "socket", label: "Socket" },
  { id: "status", label: "Status" },
  { id: "recovery", label: "Recovery" },
  { id: "api", label: "API" },
];

const activeFilters = ref<string[]>(["socket", "status", "recovery", "api"]);

const toggleFilter = (filterId: string) => {
  const index = activeFilters.value.indexOf(filterId);
  if (index === -1) {
    activeFilters.value.push(filterId);
  } else {
    activeFilters.value.splice(index, 1);
  }
};

const filteredEvents = computed(() => {
  if (activeFilters.value.length === filters.length) {
    return [...events.value].reverse();
  }
  return [...events.value].filter((e) => activeFilters.value.includes(e.type)).reverse();
});

// Auto-scroll to bottom when new events arrive
watch(
  () => events.value.length,
  async () => {
    await nextTick();
    if (eventsScrollRef.value) {
      eventsScrollRef.value.scrollTop = 0;
    }
  }
);

// Format time
const formatTime = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// Format payload
const formatPayload = (payload: any) => {
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
};

// Export events
const exportEvents = () => {
  const data = JSON.stringify(events.value, null, 2);
  const blob = new Blob([data], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ai-assistant-events-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);
};
</script>

<style scoped>
.events-tab {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* Critical for flex child scrolling */
  gap: 12px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.events-tab::-webkit-scrollbar {
  width: 6px;
}

.events-tab::-webkit-scrollbar-track {
  background: transparent;
}

.events-tab::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

/* Controls */
.events-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.filter-group {
  display: flex;
  gap: 4px;
}

.filter-btn {
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 500;
  color: #9090a0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.filter-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #c0c0d0;
}

.filter-btn.active {
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
  color: #60a5fa;
}

.action-group {
  display: flex;
  gap: 4px;
}

.icon-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  color: #9090a0;
  cursor: pointer;
  transition: all 0.15s ease;
}

.icon-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #c0c0d0;
}

/* Events List */
.events-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.event-item {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  overflow: hidden;
}

.event-item.socket {
  border-left: 2px solid #60a5fa;
}

.event-item.status {
  border-left: 2px solid #a78bfa;
}

.event-item.recovery {
  border-left: 2px solid #f59e0b;
}

.event-item.api {
  border-left: 2px solid #22c55e;
}

.event-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.02);
}

.event-type-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 3px 6px;
  border-radius: 3px;
  letter-spacing: 0.3px;
}

.event-type-badge.socket {
  background: rgba(96, 165, 250, 0.15);
  color: #60a5fa;
}

.event-type-badge.status {
  background: rgba(167, 139, 250, 0.15);
  color: #a78bfa;
}

.event-type-badge.recovery {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
}

.event-type-badge.api {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.event-name {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: #e0e0f0;
}

.event-time {
  font-size: 11px;
  color: #808098;
  font-family: "JetBrains Mono", "Fira Code", monospace;
}

.event-payload {
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.3);
}

.event-payload pre {
  margin: 0;
  font-size: 11px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #7dd3fc;
  white-space: pre-wrap;
  word-break: break-all;
}

/* Empty State */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #707090;
  padding: 32px;
}

.empty-state svg {
  opacity: 0.5;
}

.empty-state span {
  font-size: 13px;
}

.empty-state .hint {
  font-size: 11px;
  color: #606080;
}

/* Stats */
.events-stats {
  display: flex;
  gap: 20px;
  padding: 12px 14px;
  margin-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0 0 8px 8px;
  position: sticky;
  bottom: 0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(20, 20, 35, 0.95) 0%, rgba(15, 15, 26, 0.98) 100%);
  backdrop-filter: blur(8px);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.2);
}

.stat {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #c0c0d0;
}

.stat-label {
  font-size: 11px;
  color: #808098;
  text-transform: lowercase;
}
</style>


